import React from 'react';
import { Translate, I18n } from 'react-redux-i18n';

import { TemplateImageUpload } from '../../../../components/template/templateImageUpload';
import { TemplatePage } from '../../../../components/common/pager';
import { TemplateAutocomplete } from '../../../../components/template/templateAutocomplete';
import { TemplateCurrency } from '../../../../components/template/templateCurrency';
import { TemplateHeader } from '../../../../components/template/templateHeader';
import { TemplateTextArea } from '../../../../components/template/templateTextArea';
import { TemplateDropdown } from '../../../../components/template/templateDropdown';
import { TemplateDatePicker } from '../../../../components/template/templateDatePicker';

import { formatDateByCountry } from '../../../../helpers/dateAndTimeHelper';

import CurrencyHelper from '../../../../helpers/currencyHelper';

import {
  entityTypes,
  imageFormats,
  uploadTypes
} from '../../../../constants/imageUpload';
import { paths } from '../constants/template';
import config from '../../../../constants';

import { defaultAmbassadorStatus, pageCreationStatus } from '../../constants';
import { TemplateToggle } from '../../../../components/template/templateToggle';
import { OrganizationCurrencyWarning } from '../components/organizationCurrencyWarning';

const i18nBasePath = 'eventPrograms.template.page.setup.default-ambassador-status';

const getDefaultAmbassadorStatusOptions = (pageCreationOption) => {
  const isWaitlisted = pageCreationOption === pageCreationStatus.WAITLISTED;
  const waitlistTextKey = isWaitlisted
    ? `${i18nBasePath}.waitlistedWithPageCustomText`
    : `${i18nBasePath}.waitlistedWithoutPageCustomText`;
  const statusReceivedKey = `${i18nBasePath}.${defaultAmbassadorStatus.RECEIVED}`;
  const statusWaitlistedKey = `${i18nBasePath}.${defaultAmbassadorStatus.WAITLISTED}`;

  return [
    {
      value: defaultAmbassadorStatus.RECEIVED,
      text: `${I18n.t(statusReceivedKey)} - ${I18n.t(`${i18nBasePath}.receivedCustomText`)}`,
    },
    {
      value: defaultAmbassadorStatus.WAITLISTED,
      text: `${I18n.t(statusWaitlistedKey)} - ${I18n.t(waitlistTextKey)}`,
    },
  ];
};

export const getSetupTab = (props) => {
  const {
    onChange,
    required,
    target,
    optional,
    dateValidation,
    getEventAmbassadorProgramRequest,
    getEventAmbassadorProgramItem,
    getOrganizationRequest,
    onOrganizationChange,
    getOrganizationItem,
    onEventAmbassadorProgramChange,
    urlLink,
    inheritedMinimumTargetAmount,
    effectiveMinimumTargetAmount,
    minimumTargetCurrency,
    organizationCurrencyConfigured,
    selectedOrganizationCurrency,
    notFalse,
    eventApplicationsOpenDate,
    eventDefaultAmbassadorStatus,
    eventAmbassadorProgramFundraisingDeadlineDate,
    organizationCountry,
    eventAmbassadorProgram,
    isSysAdmin,
  } = props;

  const overridesAllowed = eventAmbassadorProgram.allowCampaignFundraiserOverrides;
  const hasInheritedMinimum = typeof inheritedMinimumTargetAmount === 'number';
  const showEffectiveMinimum =
    overridesAllowed === false && typeof effectiveMinimumTargetAmount === 'number';
  const minimumTargetSubtextKey = !minimumTargetCurrency
    ? 'eventPrograms.template.page.setup.minimum-fundraising-target.subtext-no-currency'
    : hasInheritedMinimum
      ? 'eventPrograms.template.page.setup.minimum-fundraising-target.subtext'
      : 'eventPrograms.template.page.setup.minimum-fundraising-target.subtext-no-minimum';

  return (
    <TemplatePage id="setup" i18nKey="eventPrograms.template.page.setup.title">
      <TemplateHeader
        as="h2"
        path="edit-program-header"
        label="eventPrograms.template.page.setup.edit-program-content.header"
      />

      <div path={paths.EDIT_PROGRAM_SUBHEADER}>
        <span>
          <Translate value="eventPrograms.template.page.setup.edit-program-content.subheader" />
          <a target={urlLink} href={urlLink}>
            <Translate value="eventPrograms.template.page.setup.edit-program-content.link" />
          </a>
        </span>
      </div>

      <TemplateImageUpload
        id="setupProgramHeroImage"
        path={paths.BANNER}
        entity={entityTypes.EVENT}
        uploadType={uploadTypes.HERO_IMAGE}
        position="top"
        height={500}
        width={1500}
        label="eventPrograms.template.page.setup.hero-image.label"
        onChange={onChange}
      />

      <TemplateTextArea
        id="setupVipProgramDescription"
        path={paths.INTRO_TEXT}
        label="eventPrograms.template.page.setup.intro-text.label"
        subtext="eventPrograms.template.page.setup.intro-text.subtext"
        validation={optional}
        onChange={onChange}
        maxLength={config.VIP_PROGRAM_INTRO_TEXT_MAX_LENGTH}
      />

      <TemplateAutocomplete
        id="vipProgram"
        entity="EventAmbassadorProgram"
        path={paths.EVENT_AMBASSADOR_PROGRAM_ID}
        selected="EventAmbassadorProgram"
        label="eventPrograms.template.page.setup.vip-program-name.label"
        searchOnLoad={false}
        initialSelectedItem={eventAmbassadorProgram}
        onChange={onEventAmbassadorProgramChange}
        request={getEventAmbassadorProgramRequest}
        getItem={getEventAmbassadorProgramItem}
        validation={required}
      />

      <TemplateAutocomplete
        id="vipOrganization"
        entity="page/campaign"
        path={paths.ORGANIZATION_ID}
        selected="campaign"
        returnField="organizationId"
        label="eventPrograms.template.page.setup.organization-name.label"
        searchOnLoad={false}
        onChange={onOrganizationChange}
        request={getOrganizationRequest}
        getItem={getOrganizationItem}
        validation={required}
      />

      <OrganizationCurrencyWarning
        path={paths.ORGANIZATION_CURRENCY_CONFIGURED}
        validation={notFalse}
        configured={organizationCurrencyConfigured}
        currency={selectedOrganizationCurrency}
      />

      {isSysAdmin && (
        <TemplateDatePicker
          id="setupApplicationsOpenDate"
          path={paths.APPLICATIONS_OPEN_DATE}
          label="eventPrograms.template.page.setup.applications-open-date.label"
          subtext="eventPrograms.template.page.setup.applications-open-date.subtext"
          subtextTranslationObject={{ date: formatDateByCountry(eventApplicationsOpenDate) }}
          systemOnly
          onChange={onChange}
          validation={dateValidation}
        />
      )}

      <TemplateCurrency
        id="setupAvailableSeats"
        path={paths.VIP_PLACES_ALLOCATED}
        validation={target}
        hideInputLabel
        label="eventPrograms.template.page.setup.vip-places-allocated-name.label"
        onChange={onChange}
      />

      <TemplateDropdown
        path={paths.DEFAULT_AMBASSADOR_STATUS}
        label="eventPrograms.template.page.setup.default-ambassador-status.label"
        subtext="eventPrograms.template.page.setup.default-ambassador-status.subtext"
        subtextTranslationObject={{ status: eventDefaultAmbassadorStatus }}
        onChange={onChange}
        options={getDefaultAmbassadorStatusOptions(eventAmbassadorProgram.pageCreationOption)}
        clearable
      />

      {showEffectiveMinimum && (
        <div path={paths.EFFECTIVE_MINIMUM_TARGET_INFO} className="field">
          <label>
            <Translate value="eventPrograms.template.page.setup.effective-minimum-fundraising-target.label" />
          </label>
          <div className="field-value">
            <Translate
              value="eventPrograms.template.page.setup.effective-minimum-fundraising-target.content"
              amount={CurrencyHelper.formatCurrency(effectiveMinimumTargetAmount, minimumTargetCurrency)}
              currency={minimumTargetCurrency}
            />
          </div>
        </div>
      )}

      <TemplateToggle customValue={overridesAllowed}>
        <TemplateCurrency
          id="setupMinimumFundraisingTarget"
          path={paths.FUNDRAISER_MINIMUM_TARGET_AMOUNT}
          validation={{
            required: false,
            amount: true,
            minValue: hasInheritedMinimum ? inheritedMinimumTargetAmount : undefined,
          }}
          label="eventPrograms.template.page.setup.minimum-fundraising-target.label"
          subtext={minimumTargetSubtextKey}
          subtextTranslationObject={{
            eventMinimumTargetAmount: hasInheritedMinimum
              ? CurrencyHelper.formatCurrency(inheritedMinimumTargetAmount, minimumTargetCurrency)
              : '',
            currency: minimumTargetCurrency,
          }}
          onChange={onChange}
          currency={minimumTargetCurrency}
        />

        <TemplateDatePicker
          id="setupFundraisingDeadline"
          path={paths.FUNDRAISING_DEADLINE_DATE}
          label="eventPrograms.template.page.setup.fundraising-deadline-date.label"
          subtext="eventPrograms.template.page.setup.fundraising-deadline-date.subtext"
          subtextTranslationObject={{
            eventFundraisingDeadline: formatDateByCountry(eventAmbassadorProgramFundraisingDeadlineDate, organizationCountry)
          }}
          onChange={onChange}
          validation={dateValidation}
        />
      </TemplateToggle>
    </TemplatePage>
  );
};
